import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Spinner, Alert, Button, Form } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { API_BASE, IMAGE_BASE } from "../Config";

const NewsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [news, setNews] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [newsTypeId, setNewsTypeId] = useState<string | null>(null);

  useEffect(() => {
    fetchNewsTypeId();
  }, []);

  useEffect(() => {
    if (newsTypeId) {
      fetchNews();
    }
  }, [newsTypeId, page]);

  const fetchNewsTypeId = async () => {
    try {
      setLoading(true);
      
      // Get content type ID for news
      const contentTypesResponse = await fetch(`${API_BASE}/contenttypes`);
      if (!contentTypesResponse.ok) throw new Error("Failed to fetch content types");
      
      const contentTypes = await contentTypesResponse.json();
      // Find the news content type by name
      const newsType = contentTypes.find((type: any) => type.name.toLowerCase() === "news");
      
      if (!newsType) {
        setNews([]);
        throw new Error("News content type not found");
      }
      
      setNewsTypeId(newsType._id);
    } catch (err) {
      console.error(err);
      setError("Failed to load news");
    } finally {
      setLoading(false);
    }
  };

  const fetchNews = async () => {
    try {
      if (!newsTypeId) return;
      
      if (page === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      
      // Fetch news by type ID
      const response = await fetch(
        `${API_BASE}/contents?contentType=${newsTypeId}&isActive=true&limit=6&page=${page}&sort=-createdAt`
      );
      
      if (!response.ok) throw new Error("Failed to fetch news");
      
      const data = await response.json();
      
      if (page === 1) {
        setNews(data.contents || []);
      } else {
        setNews(prev => [...prev, ...(data.contents || [])]);
      }
      
      // Check if there are more pages
      setHasMore(data.currentPage < data.totalPages);
    } catch (err) {
      console.error(err);
      setError("Failed to load news");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Fetch news by type ID and search query
      const response = await fetch(
        `${API_BASE}/contents?contentType=${newsTypeId}&isActive=true&search=${encodeURIComponent(searchQuery)}`
      );
      
      if (!response.ok) throw new Error("Failed to search news");
      
      const data = await response.json();
      setNews(data.contents || []);
      setHasMore(false);
    } catch (err) {
      console.error(err);
      setError("Failed to search news");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleNewsClick = (id: string) => {
    navigate(`/article/${id}`);
  };

  return (
    <>
      <Helmet>
        <title>Latest News | Natakiya - Theater News & Updates</title>
        <meta name="description" content="Stay updated with the latest theater news, announcements, and updates from the performing arts community on Natakiya." />
        
        {/* Open Graph meta tags */}
        <meta property="og:title" content="Latest News | Natakiya - Theater News & Updates" />
        <meta property="og:description" content="Stay updated with the latest theater news, announcements, and updates from the performing arts community on Natakiya." />
        <meta property="og:image" content={`${window.location.origin}/logo.png`} />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Natakiya" />
        
        {/* Twitter Card meta tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Latest News | Natakiya - Theater News & Updates" />
        <meta name="twitter:description" content="Stay updated with the latest theater news, announcements, and updates from the performing arts community on Natakiya." />
        <meta name="twitter:image" content={`${window.location.origin}/logo.png`} />
      </Helmet>
      
      <div style={{ background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)', minHeight: '100vh' }}>
      <Container className="py-5">
        {/* Modern Header */}
        <div className="text-center mb-5">
          <div className="d-inline-flex align-items-center mb-3">
            <div 
              className="rounded-circle d-flex align-items-center justify-content-center me-3"
              style={{ width: '60px', height: '60px', background: 'linear-gradient(135deg, #FFC107 0%, #FF8F00 100%)' }}
            >
              <i className="bi bi-newspaper text-white" style={{ fontSize: '1.5rem' }}></i>
            </div>
            <div>
              <h1 className="display-5 fw-bold mb-0" style={{ color: '#FF8F00' }}>Latest News</h1>
              <p className="text-muted mb-0">Stay updated with the latest news and announcements</p>
            </div>
          </div>
        </div>
        
        {/* Modern Search Section */}
        <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: '20px' }}>
          <Card.Body className="p-4">
            <Row className="justify-content-center">
              <Col lg={8}>
                <div className="position-relative">
                  <Form.Control
                    type="text"
                    placeholder="Search news by title or content..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={handleKeyPress}
                    className="ps-5 py-3 border-0 bg-light"
                    style={{ borderRadius: '50px', fontSize: '16px' }}
                  />
                  <i 
                    className="bi bi-search position-absolute" 
                    style={{ left: '20px', top: '50%', transform: 'translateY(-50%)', color: '#FF8F00' }}
                  ></i>
                  <Button 
                    onClick={handleSearch} 
                    disabled={loading}
                    className="position-absolute border-0"
                    style={{ 
                      right: '8px', 
                      top: '50%', 
                      transform: 'translateY(-50%)',
                      backgroundColor: '#FFC107',
                      color: '#212121',
                      borderRadius: '50px',
                      width: '40px',
                      height: '40px'
                    }}
                  >
                    {loading ? <Spinner size="sm" /> : <i className="bi bi-search"></i>}
                  </Button>
                </div>
              </Col>
            </Row>
            
            {/* Active Search Pill */}
            {searchQuery && (
              <div className="mt-3 d-flex justify-content-center">
                <span 
                  className="badge d-flex align-items-center"
                  style={{ backgroundColor: '#FF8F00', borderRadius: '50px', padding: '8px 12px' }}
                >
                  Search: "{searchQuery}"
                  <button 
                    className="btn-close btn-close-white ms-2" 
                    style={{ fontSize: '10px' }}
                    onClick={() => {
                      setSearchQuery("");
                      fetchNews();
                    }}
                  ></button>
                </span>
              </div>
            )}
          </Card.Body>
        </Card>
        
        {/* Results Header */}
        {!loading && (
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h5 className="mb-1" style={{ color: '#FF8F00' }}>
                {news.length > 0 ? `${news.length} News Articles Found` : "No News Found"}
              </h5>
              <small className="text-muted">
                {searchQuery && `Searching for "${searchQuery}"`}
              </small>
            </div>
          </div>
        )}

      {error && (
          <Alert 
            variant="" 
            className="border-0 text-center mb-4"
            style={{ 
              backgroundColor: 'rgba(255, 193, 7, 0.1)', 
              color: '#FF8F00',
              borderRadius: '15px',
              padding: '20px'
            }}
          >
            <i className="bi bi-exclamation-triangle-fill me-2" style={{ color: '#FFC107' }}></i>
            {error}
          </Alert>
        )}

      {/* Modern News Grid */}
        <Row className="g-4">
          {!loading && news.map((item) => (
            <Col key={item._id} lg={6} md={6}>
              <Card 
                className="h-100 border-0" 
                onClick={() => handleNewsClick(item._id)}
                style={{ 
                  cursor: "pointer", 
                  borderRadius: '20px',
                  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                  boxShadow: '0 4px 20px rgba(255, 143, 0, 0.1)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-8px) scale(1.02)";
                  e.currentTarget.style.boxShadow = "0 20px 40px rgba(255, 143, 0, 0.2)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0) scale(1)";
                  e.currentTarget.style.boxShadow = "0 4px 20px rgba(255, 143, 0, 0.1)";
                }}
              >
                <div className="position-relative overflow-hidden" style={{ borderRadius: '20px 20px 0 0' }}>
                  {item.imageLink ? (
                    <Card.Img 
                      variant="top" 
                      src={IMAGE_BASE + item.imageLink} 
                      style={{ height: "200px", objectFit: "cover" }}
                    />
                  ) : (
                    <div 
                      className="d-flex align-items-center justify-content-center"
                      style={{ height: "200px", background: "linear-gradient(135deg, #FFC107 0%, #FF8F00 100%)" }}
                    >
                      <i className="bi bi-newspaper text-white" style={{ fontSize: "2.5rem" }}></i>
                    </div>
                  )}
                  <div className="position-absolute top-0 start-0 w-100 h-100" 
                      style={{ background: 'linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.1) 100%)' }}></div>
                </div>
                
                <Card.Body className="d-flex flex-column p-4">
                  <Card.Title className="fw-bold mb-2" style={{ color: '#FF8F00', fontSize: '1.1rem' }}>
                    {item.title}
                  </Card.Title>
                  <Card.Text className="text-muted flex-grow-1" style={{ fontSize: "0.9rem" }}>
                    {item.content && item.content.length > 150 
                      ? `${item.content.substring(0, 150).replace(/<[^>]*>/g, '')}...` 
                      : item.content?.replace(/<[^>]*>/g, '') || "Click to read this news article."}
                  </Card.Text>
                  
                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <small className="text-muted">
                      <i className="bi bi-calendar me-1"></i>
                      {formatDate(item.createdAt)}
                    </small>
                    
                    {/* Writer Pill */}
                    {item.writer && (
                      <span 
                        className="badge d-inline-flex align-items-center"
                        style={{ 
                          backgroundColor: 'rgba(255, 143, 0, 0.1)', 
                          color: '#FF8F00', 
                          borderRadius: '50px',
                          padding: '6px 12px',
                          fontSize: '0.8rem'
                        }}
                      >
                        <i className="bi bi-person me-1"></i>
                        {item.writer}
                      </span>
                    )}
                  </div>
                  
                  {/* Action Button */}
                  <div className="d-flex justify-content-end mt-3">
                    <small style={{ color: '#FFC107', fontWeight: '600' }}>
                      Read More <i className="bi bi-arrow-right ms-1"></i>
                    </small>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
        
        {/* Loading Spinner */}
        {loading && (
          <div className="text-center py-5">
            <Spinner animation="border" role="status" style={{ color: '#FF8F00' }} />
            <p className="mt-3 text-muted">Loading news...</p>
          </div>
        )}
        
        {/* Modern Empty State */}
        {!loading && news.length === 0 && !error && (
          <Card className="border-0 text-center py-5" style={{ borderRadius: '20px', background: 'rgba(255, 255, 255, 0.8)' }}>
            <Card.Body className="py-5">
              <div 
                className="rounded-circle d-inline-flex align-items-center justify-content-center mb-4"
                style={{ 
                  width: '80px', 
                  height: '80px', 
                  background: 'rgba(255, 193, 7, 0.2)'
                }}
              >
                <i className="bi bi-newspaper" style={{ fontSize: '2rem', color: '#FF8F00' }}></i>
              </div>
              <h4 className="mb-3" style={{ color: '#FF8F00' }}>No News Found</h4>
              <p className="text-muted mb-4" style={{ maxWidth: '400px', margin: '0 auto' }}>
                {searchQuery 
                  ? "We couldn't find any news matching your search. Try adjusting your criteria." 
                  : "Check back soon for the latest news and updates!"}
              </p>
              {searchQuery && (
                <Button 
                  onClick={() => {
                    setSearchQuery("");
                    fetchNews();
                  }}
                  className="rounded-pill border-0 px-4 py-2"
                  style={{ backgroundColor: '#FFC107', color: '#212121', fontWeight: '600' }}
                >
                  <i className="bi bi-arrow-clockwise me-2"></i>
                  Clear Search
                </Button>
              )}
            </Card.Body>
          </Card>
        )}
      
      {/* Modern Load More Button */}
        {news.length > 0 && hasMore && !searchQuery && (
          <div className="text-center mt-5">
            <Button 
              onClick={() => setPage(p => p + 1)} 
              disabled={loadingMore}
              className="rounded-pill border-0 px-5 py-3"
              style={{ 
                background: 'linear-gradient(135deg, #FFC107 0%, #FF8F00 100%)',
                color: 'white',
                fontWeight: '600',
                fontSize: '16px',
                boxShadow: '0 4px 15px rgba(255, 143, 0, 0.3)'
              }}
            >
              {loadingMore ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Loading More...
                </>
              ) : (
                <>
                  <i className="bi bi-plus-circle me-2"></i>
                  Load More News
                </>
              )}
            </Button>
          </div>
        )}
      </Container>
      </div>
    </>
  );
};

export default NewsPage;